import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';
import { getFormValues, isInvalid } from 'redux-form';
import moment from 'moment';
import { initialState } from './reducer';
import {
	DATE_FORMAT,
	THERMOBOX_REPORT_DOCUMENTS_FORM_NAME,
	THERMOBOX_REPORT_FORM_NAME,
	THERMOBOX_REPORT_TABLE_ROWS_NAMES,
} from './constants';
import { thousandSeparator } from '../reqs-filter/utils';

export const selectThermoboxReportModalDomain = state => state.thermoboxReportModalReducer || initialState;
export const selectRouterDomain = state => state.router || {};
export const selectProfileModalDomain = state => state.profileModalReducer || {};

export const getThermoboxReportInfoSelector = () =>
	createSelector(selectThermoboxReportModalDomain, ({ thermoboxReportInfo }) => {
		if (!thermoboxReportInfo) return [];

		return Object.entries(THERMOBOX_REPORT_TABLE_ROWS_NAMES).reduce((result, [key, value]) => {
			if (key === 'date') {
				result.push({
					id: key,
					title: value,
					value: thermoboxReportInfo.date ? moment(thermoboxReportInfo.date).format(DATE_FORMAT) : '-',
				});

				return result;
			}

			if (key === 'thermobox') {
				result.push({
					id: key,
					title: value,
					value: {
						name: thermoboxReportInfo?.thermobox?.name || '',
						partner: thermoboxReportInfo?.thermobox?.partner?.name || '',
					},
				});

				return result;
			}

			if (key === 'mode') {
				result.push({
					id: key,
					title: value,
					value: thermoboxReportInfo.mode.name_with_symbol || '-',
				});

				return result;
			}

			if (key === 'holdTime') {
				result.push({
					id: key,
					title: value,
					value: thermoboxReportInfo.hold_time || '-',
				});

				return result;
			}

			if (key === 'outerMode') {
				result.push({
					id: key,
					title: value,
					value: thermoboxReportInfo.outer_mode ? `${thermoboxReportInfo.outer_mode} °C` : '-',
				});

				return result;
			}

			if (key === 'inner') {
				result.push({
					id: key,
					title: value,
					value: {
						length: thermoboxReportInfo.inner_length
							? thousandSeparator(thermoboxReportInfo.inner_length)
							: '',
						width: thermoboxReportInfo.inner_width
							? thousandSeparator(thermoboxReportInfo.inner_width)
							: '',
						height: thermoboxReportInfo.inner_height
							? thousandSeparator(thermoboxReportInfo.inner_height)
							: '',
					},
				});

				return result;
			}

			if (key === 'innerVolume') {
				result.push({
					id: key,
					title: value,
					value: thermoboxReportInfo.inner_volume
						? `${thousandSeparator(thermoboxReportInfo.inner_volume, 3)} м³`
						: '-',
				});

				return result;
			}

			if (key === 'palletWeight') {
				result.push({
					id: key,
					title: value,
					value: thermoboxReportInfo.pallet_weight
						? `${thousandSeparator(thermoboxReportInfo.pallet_weight)} кг`
						: '-',
				});

				return result;
			}

			if (key === 'author') {
				result.push({
					id: key,
					title: value,
					value: {
						name: `${thermoboxReportInfo?.user?.last_name || ''} ${
							thermoboxReportInfo?.user?.first_name || ''
						}`,
						role: thermoboxReportInfo?.user?.role?.name || '',
					},
				});

				return result;
			}

			if (key === 'icepacks') {
				// eslint-disable-next-line array-callback-return
				thermoboxReportInfo?.icepacks.map((icepack, index) => {
					result.push({
						group: `${value} №${index + 1}`,
					});

					result.push({
						id: `${key}.${index}.icepack`,
						title: 'Название',
						value: icepack?.icepack_filled?.icepack?.name ?? '-',
					});

					result.push({
						id: `${key}.${index}.filler`,
						title: 'Тип наполнителя',
						value: icepack?.icepack_filled?.filler?.name ?? '-',
					});

					result.push({
						id: `${key}.${index}.state`,
						title: 'Состояние наполнителя',
						value: icepack?.icepack_filled?.state?.name ?? '-',
					});

					result.push({
						id: `${key}.${index}.mode`,
						title: 'Температурный режим',
						value: icepack?.icepack_filled?.mode?.name_with_symbol ?? '-',
					});

					result.push({
						id: `${key}.${index}.count`,
						title: 'Кол-во',
						value: icepack?.count ? `${icepack?.count} шт` : '-',
					});
				});

				return result;
			}

			result.push({
				id: key,
				title: value,
				value: thermoboxReportInfo[key] ?? '-',
			});

			return result;
		}, []);
	});

export const getIsThermoboxReportInfoLoadingSelector = () =>
	createSelector(
		selectThermoboxReportModalDomain,
		({ isThermoboxReportInfoLoading }) => isThermoboxReportInfoLoading,
	);

export const getIsThermoboxReportCreatingSelector = () =>
	createSelector(selectThermoboxReportModalDomain, ({ isThermoboxReportCreating }) => isThermoboxReportCreating);

export const getIsThermoboxReportUpdatingSelector = () =>
	createSelector(selectThermoboxReportModalDomain, ({ isThermoboxReportUpdating }) => isThermoboxReportUpdating);

export const getIsThermoboxReportDeletingSelector = () =>
	createSelector(selectThermoboxReportModalDomain, ({ isThermoboxReportDeleting }) => isThermoboxReportDeleting);

export const getFormInitialValuesSelector = () =>
	createSelector(selectThermoboxReportModalDomain, selectProfileModalDomain, ({ thermoboxReportInfo }) => {
		return {
			...thermoboxReportInfo,
			icepacks: thermoboxReportInfo?.icepacks.map(icepack => ({
				...icepack,
				icepack_filled_id: icepack?.icepack_filled?.id,
			})),
			thermobox_id: thermoboxReportInfo?.thermobox?.id,
			mode_id: thermoboxReportInfo?.mode?.id,
		};
	});

export const getFormIsInvalidSelector = () =>
	createSelector(isInvalid(THERMOBOX_REPORT_FORM_NAME), isInvalid => isInvalid);

export const getDataForSaveSelector = () =>
	createSelector(getFormValues(THERMOBOX_REPORT_FORM_NAME), formValues => formValues);

export const getThermoboxReportNameSelector = () =>
	createSelector(selectThermoboxReportModalDomain, ({ thermoboxReportInfo }) => thermoboxReportInfo?.number ?? '');

export const getIsModalShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'thermobox_report');
	});

export const getIsThermoboxReportAddFormOpenSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'thermobox_report' && query?.mode === 'add');
	});

export const getIsThermoboxReportEditFormOpenSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'thermobox_report' && query?.mode === 'edit');
	});

export const getActiveThermoboxReportIdSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return query?.modal === 'thermobox_report' ? query?.id : null;
	});

export const getIsAuditTabShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'thermobox_report' && query?.id && query?.tab === 'audit');
	});

export const getIsDocsTabShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'thermobox_report' && query?.id && query?.tab === 'docs');
	});

export const getThermoboxesOptionsSelector = () =>
	createSelector(selectThermoboxReportModalDomain, ({ thermoboxes }) =>
		thermoboxes.map(thermobox => ({
			key: thermobox.id,
			value: thermobox.id,
			text: thermobox.name,
		})),
	);

export const getIsThermoboxesLoadingSelector = () =>
	createSelector(selectThermoboxReportModalDomain, ({ isThermoboxesLoading }) => isThermoboxesLoading);

export const getModesOptionsSelector = () =>
	createSelector(selectThermoboxReportModalDomain, ({ modes }) =>
		modes.map(mode => ({
			key: mode.id,
			value: mode.id,
			text: mode.name_with_symbol,
		})),
	);

export const getIsModesLoadingSelector = () =>
	createSelector(selectThermoboxReportModalDomain, ({ isModesLoading }) => isModesLoading);

export const getIcepacksOptionsSelector = () =>
	createSelector(selectThermoboxReportModalDomain, ({ icepacks }) =>
		icepacks.map(icepack => ({
			key: icepack.id,
			value: icepack.id,
			text: `${icepack?.icepack?.name ?? '-'}, ${icepack?.filler?.name ?? '-'}, ${icepack?.state?.name ?? '-'}, ${
				icepack?.mode?.name_with_symbol ?? '-'
			}`,
		})),
	);

export const getIsIcepacksLoadingSelector = () =>
	createSelector(selectThermoboxReportModalDomain, ({ isIcepacksLoading }) => isIcepacksLoading);

export const getThermoboxReportDocsSelector = () =>
	createSelector(selectThermoboxReportModalDomain, ({ thermoboxReportDocs }) => thermoboxReportDocs);

export const getIsThermoboxReportDocsLoadingSelector = () =>
	createSelector(
		selectThermoboxReportModalDomain,
		({ isThermoboxReportDocsLoading }) => isThermoboxReportDocsLoading,
	);

export const getIsThermoboxReportDocsSendingSelector = () =>
	createSelector(
		selectThermoboxReportModalDomain,
		({ isThermoboxReportDocsSending }) => isThermoboxReportDocsSending,
	);

export const getThermoboxReportFilesSelector = () =>
	createSelector(selectThermoboxReportModalDomain, ({ files }) => files);

export const getThermoboxReportFormSelector = () =>
	createSelector(selectThermoboxReportModalDomain, ({ thermoboxReportForm }) => thermoboxReportForm);

export const getThermoboxReportDocumentsErrorsSelector = () =>
	createSelector(
		selectThermoboxReportModalDomain,
		({ thermoboxReportDocsFormErrors }) => thermoboxReportDocsFormErrors,
	);

export const getIsThermoboxReportDocumentsModalShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query.modal === 'thermobox_report_attachment');
	});

export const getDataForSaveDocsSelector = () =>
	createSelector(
		getThermoboxReportFilesSelector(),
		getFormValues(THERMOBOX_REPORT_DOCUMENTS_FORM_NAME),
		(files, formValues) => {
			const formData = new FormData();

			Object.entries(files || {}).forEach(([key, value]) => {
				value.forEach(file => {
					formData.append(`${key}[]`, file.file);
				});
			});
			if (formValues?.document_name) {
				formData.append('document_name', formValues.document_name);
			}

			return formData;
		},
	);
